import React, { useCallback } from 'react';
import { useDebounce } from 'react-use';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useQueryClient } from '@tanstack/react-query';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
// eslint-disable-next-line max-len
import { AutocompleteResult } from 'candidate_profile_redesign/rightbar/application_details/mapbox/autocomplete_result';
import { Geolocation } from 'talent_matching/types';
import {
  SimpleSingleDropdown,
  SingleDropdown,
  useSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.location');

const RADIUS_OPTIONS = ['5', '10', '25', '50', '100'];

type LocationContentProps = {
  readOnly?: boolean;
};

export const LocationContent = ({ readOnly = false }: LocationContentProps) => {
  const { setValue, watch } = useFormContext();
  const queryClient = useQueryClient();
  const {
    paths: { mapboxSuggestionsPath },
  } = useTalentMatchingTabContext();

  const locationFilterType: 'name' | 'radius' = watch('locationFilterType');
  const locationName: string = watch('locationName');
  const locationRadius: string | null = watch('geolocationRadius');
  const selectedLocation: Geolocation | null = watch('geolocation');

  useDebounce(
    () => {
      setValue('debouncedLocationName', locationName, { shouldDirty: true });
    },
    500,
    [locationName]
  );

  const setLocationFilterType = (type: 'name' | 'radius') => {
    if (readOnly) {
      return;
    }
    setValue('locationFilterType', type, { shouldDirty: true });
  };

  const radiusOptions = RADIUS_OPTIONS.map((radius) => {
    return {
      label: t('radius', { radius: radius }),
      value: radius,
    };
  });

  const selectedRadius = radiusOptions.find(
    (option) => option.value === locationRadius
  ) as Item;

  const fetchMapboxSuggestions = useCallback(
    async (term: string) => {
      if (term.length < 3) {
        return [];
      }

      const response = await standardRequestWithErrorFlash.get(
        mapboxSuggestionsPath,
        {
          params: { term: encodeURIComponent(term) },
        }
      );

      return response.data.results.map(
        (f: Record<string, unknown>) =>
          new AutocompleteResult(f) as unknown as Geolocation
      );
    },
    [mapboxSuggestionsPath]
  );

  const fetchItems = useCallback(
    async (term: string) => {
      if (term.length < 3) {
        return { items: [] };
      }

      const queryKey = ['locations', term];
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchMapboxSuggestions(term),
      });

      return { items };
    },
    [queryClient, fetchMapboxSuggestions]
  );

  const onSelectedLocationChange = (val: { selectedItem?: Item | null }) => {
    if (readOnly) {
      return;
    }
    setValue('geolocation', val.selectedItem, { shouldDirty: true });
  };

  const dropdownProps = useSingleDropdown({
    fetchItems,
    onSelectedItemChange: onSelectedLocationChange,
    selectedItem: selectedLocation,
    debounceWait: 1000,
  });

  const radioOptions = [
    {
      label: t('by_radius'),
      value: 'radius',
    },
    {
      label: t('by_name'),
      value: 'name',
    },
  ];

  return (
    <Stack spacing={2} direction="column">
      <RadioGroup
        ariaLabel={t('location_filter_type')}
        options={radioOptions}
        value={locationFilterType}
        onChange={(selected) =>
          setLocationFilterType(selected as 'radius' | 'name')
        }
        direction="row"
        disabled={readOnly}
      />
      {locationFilterType === 'radius' ? (
        <Stack spacing={2} direction="row" sx={{ width: '100%' }}>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <SimpleSingleDropdown
              label={t('radius_label')}
              clearable={false}
              items={radiusOptions}
              selectedItem={selectedRadius}
              onSelectedItemChange={(radius) => {
                if (readOnly) {
                  return;
                }
                setValue('geolocationRadius', radius.value, {
                  shouldDirty: false,
                });
              }}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <SingleDropdown dropdownProps={dropdownProps}>
              <SingleDropdown.Field
                label={t('radius_location_label')}
                disabled={readOnly}
              />
              <SingleDropdown.Flyout />
            </SingleDropdown>
          </Grid>
        </Stack>
      ) : (
        <TextInput
          label={t('location_label')}
          value={locationName}
          onChange={(e) => {
            if (readOnly) {
              return;
            }
            setValue('locationName', e.target.value, { shouldDirty: true });
          }}
          disabled={readOnly}
        />
      )}
    </Stack>
  );
};

const Location = () => {
  const { watch } = useFormContext();
  const { locationFilterReadOnly } = useTalentMatchingTabContext();
  const [locationFilterType, debouncedLocationName, selectedLocation] = watch([
    'locationFilterType',
    'debouncedLocationName',
    'geolocation',
  ]) as ['name' | 'radius', string, Geolocation | null];
  const hasLocation =
    locationFilterType === 'radius'
      ? Boolean(selectedLocation)
      : Boolean(debouncedLocationName);

  const buttonLabel = `${t('title')}${hasLocation ? ' (1)' : ''}`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('title')}
      disabled={locationFilterReadOnly}
      disabledTooltip={locationFilterReadOnly ? t('read_only_tooltip') : null}
    >
      <LocationContent readOnly={locationFilterReadOnly} />
    </PopoverButton>
  );
};

export default Location;
